import axios from 'axios'
import { useSWRAxios } from '@/hooks/useSWRAxios'
import type { IDigitalFileWContent } from '@/modules/DigitalFile/DigitalFile'
import { convertDigitalFileToBase64 } from '@/utils/misc'
import {
  AUTHORITY_ENDPOINTS,
  type ESubmissionStatus,
  type IAnchorImportReport,
  type IAnchorRow,
  type IAuthorityMailFolder,
  type IAuthorityThreadDetail,
  type IAuthorityThreadParticipant,
  type IAuthorityUnreadCount,
  type IChecklistImportResult,
  type IHistoryImportResult,
  type ISubmission,
} from './authority.types'

//	///////////////////////
//	MARK: SUBMISSIONS
export async function openSubmission(
  revisionId: number,
  authorityId?: number | null,
): Promise<ISubmission> {
  const r = await axios.post<ISubmission>(AUTHORITY_ENDPOINTS.submission, {
    revisionId,
    authorityId: authorityId ?? null,
  })
  return r.data
}

export async function markSubmissionSubmitted(
  submissionId: number,
  sentAt?: number | null,
): Promise<ISubmission> {
  const r = await axios.put<ISubmission>(
    `${AUTHORITY_ENDPOINTS.submission}/${submissionId}/submitted`,
    { sentAt: sentAt ?? null },
  )
  return r.data
}

export interface ITransitionDetails {
  decisionAt?: number | null
  authorityReference?: string | null
  notes?: string | null
  closureReason?: string | null
}

export async function transitionSubmission(
  submissionId: number,
  status: ESubmissionStatus,
  details: ITransitionDetails,
): Promise<ISubmission> {
  const r = await axios.put<ISubmission>(
    `${AUTHORITY_ENDPOINTS.submission}/${submissionId}/status`,
    { status, ...details },
  )
  return r.data
}

// L'envoi réel — n'appeler qu'après le dialogue de confirmation. `to` remplace le
// destinataire pour un envoi d'essai vers sa propre adresse.
export async function sendSubmission(
  submissionId: number,
  checklistCode?: string | null,
  to?: string | null,
): Promise<ISubmission> {
  const r = await axios.post<ISubmission>(
    `${AUTHORITY_ENDPOINTS.submission}/${submissionId}/send`,
    { code: checklistCode ?? null, to: to || null },
  )
  return r.data
}

export async function saveChangeNote(
  submissionId: number,
  itemId: number,
  text: string,
): Promise<void> {
  await axios.put(`${AUTHORITY_ENDPOINTS.submission}/${submissionId}/changeNotes/${itemId}`, {
    text,
  })
}

//	///////////////////////
//	MARK: AUTHORITY MAILBOX
// La pastille de non-lus — poll léger toutes les 60 s ; même clé SWR pour le menu, le studio et
// la vue, donc un mutate() la rafraîchit partout. `enabled` évite l'appel hors rôles.
export function useAuthorityUnreadCount(enabled: boolean) {
  return useSWRAxios<IAuthorityUnreadCount>(
    enabled ? AUTHORITY_ENDPOINTS.authorityMailboxUnreadCount : null,
    { refreshInterval: 60_000 },
  )
}

export interface IAuthorityMailPayload {
  threadId?: number | null
  authorityId?: number | null
  to?: string[]
  cc?: string[]
  bcc?: string[]
  subject?: string | null
  body: string
  // Corps riche du composeur WYSIWYG — `body` reste l'alternative texte dégradée.
  htmlBody?: string | null
  files?: File[]
}

// L'envoi réel depuis la boîte Autorité — n'appeler qu'après le dialogue de confirmation.
// to/cc/bcc partent en tableaux JSON, ou en CSV dans le multipart (contrat post_send).
export async function sendAuthorityMail(
  payload: IAuthorityMailPayload,
): Promise<IAuthorityThreadDetail> {
  if (payload.files && payload.files.length > 0) {
    const form = new FormData()
    if (payload.threadId != null) form.append('threadId', String(payload.threadId))
    if (payload.authorityId != null) form.append('authorityId', String(payload.authorityId))
    if (payload.to && payload.to.length > 0) form.append('to', payload.to.join(','))
    // CC/BCC : un tableau vide part quand même — il exprime « pas de copie », sans quoi le
    // serveur remettrait les intervenants CC du fil par défaut.
    if (payload.cc) form.append('cc', payload.cc.join(','))
    if (payload.bcc) form.append('bcc', payload.bcc.join(','))
    if (payload.subject) form.append('subject', payload.subject)
    form.append('body', payload.body)
    if (payload.htmlBody) form.append('htmlBody', payload.htmlBody)
    for (const file of payload.files) form.append('files', file)

    const r = await axios.post<IAuthorityThreadDetail>(
      AUTHORITY_ENDPOINTS.authorityMailboxSend,
      form,
    )
    return r.data
  }

  const body: Record<string, unknown> = { body: payload.body }
  if (payload.threadId != null) body.threadId = payload.threadId
  if (payload.authorityId != null) body.authorityId = payload.authorityId
  if (payload.to && payload.to.length > 0) body.to = payload.to
  // CC/BCC : un tableau vide part quand même — « pas de copie » explicite (voir multipart).
  if (payload.cc) body.cc = payload.cc
  if (payload.bcc) body.bcc = payload.bcc
  if (payload.subject) body.subject = payload.subject
  if (payload.htmlBody) body.htmlBody = payload.htmlBody

  const r = await axios.post<IAuthorityThreadDetail>(AUTHORITY_ENDPOINTS.authorityMailboxSend, body)
  return r.data
}

//	///////////////////////
//	MARK: FOLDERS
export async function saveAuthorityFolder(
  id: number | null,
  payload: { name?: string; parentId?: number | null; sortOrder?: number },
): Promise<IAuthorityMailFolder> {
  const r =
    id == null
      ? await axios.post<IAuthorityMailFolder>(AUTHORITY_ENDPOINTS.authorityMailboxFolders, payload)
      : await axios.put<IAuthorityMailFolder>(
          `${AUTHORITY_ENDPOINTS.authorityMailboxFolders}/${id}`,
          payload,
        )
  return r.data
}

export async function deleteAuthorityFolder(id: number): Promise<void> {
  await axios.delete(`${AUTHORITY_ENDPOINTS.authorityMailboxFolders}/${id}`)
}

// folderId null renvoie le fil aux « non classés ».
export async function moveThreadToFolder(threadId: number, folderId: number | null): Promise<void> {
  await axios.post(`${AUTHORITY_ENDPOINTS.authorityMailboxThreads}/${threadId}/move`, { folderId })
}

// Fusionne les fils sources dans la cible — messages, soumission et dossiers ; les sources
// disparaissent. Retourne le fil cible recomposé.
export async function mergeAuthorityThreads(
  targetId: number,
  sourceIds: number[],
): Promise<IAuthorityThreadDetail> {
  const r = await axios.post<IAuthorityThreadDetail>(
    `${AUTHORITY_ENDPOINTS.authorityMailboxThreads}/${targetId}/merge`,
    { sourceIds },
  )
  return r.data
}

export interface IAuthorityThreadDeleteReport {
  deleted: number
  refused: Array<{ id: number; subject: string | null; reason: string }>
  // Emails purgés de la boîte IMAP avec le fil — sans purge, le poll les ré-ingérerait.
  mailboxPurged: number
  mailboxWarnings: Array<{ id: number; subject: string | null }>
}

// Suppression irréversible — n'appeler qu'après confirmation. Les fils rattachés à une
// soumission sont refusés par le serveur (rétention réglementaire) et nommés dans le rapport.
export async function deleteAuthorityThreads(ids: number[]): Promise<IAuthorityThreadDeleteReport> {
  const r = await axios.post<IAuthorityThreadDeleteReport>(
    `${AUTHORITY_ENDPOINTS.authorityMailboxThreads}/deleteBatch`,
    { ids },
  )
  return r.data
}

export interface IAuthorityParticipantPayload {
  id?: number
  email?: string
  role?: 'TO' | 'CC'
  displayName?: string | null
  phone?: string | null
}

// Ajout ({email}) ou mise à jour ({id}) d'un intervenant — retourne la liste recomposée.
export async function saveAuthorityParticipant(
  threadId: number,
  payload: IAuthorityParticipantPayload,
): Promise<IAuthorityThreadParticipant[]> {
  const r = await axios.post<IAuthorityThreadParticipant[]>(
    `${AUTHORITY_ENDPOINTS.authorityMailboxThreads}/${threadId}/participants`,
    payload,
  )
  return r.data
}

// Retire l'intervenant des envois du fil — la resynchronisation ne le fait pas revenir.
export async function removeAuthorityParticipant(participantId: number): Promise<void> {
  await axios.post(`${AUTHORITY_ENDPOINTS.authorityMailbox}/participants/${participantId}/remove`)
}

// Masque / ré-affiche une pièce du panneau latéral — décision partagée et réversible.
export async function setAttachmentHidden(attachmentId: number, hidden: boolean): Promise<void> {
  await axios.post(`${AUTHORITY_ENDPOINTS.authorityMailbox}/attachments/${attachmentId}/hidden`, {
    hidden,
  })
}

// L'inverse du marquage automatique à l'ouverture du fil — la pastille recompte aussitôt.
export async function markMessageUnread(messageId: number): Promise<number> {
  const r = await axios.post<{ count: number }>(
    `${AUTHORITY_ENDPOINTS.authorityMailboxMessages}/${messageId}/unread`,
  )
  return r.data.count
}

export async function attachThreadToSubmission(
  threadId: number,
  submissionId: number,
): Promise<IAuthorityThreadDetail> {
  const r = await axios.post<IAuthorityThreadDetail>(
    `${AUTHORITY_ENDPOINTS.authorityMailboxThreads}/${threadId}/attachSubmission`,
    { submissionId },
  )
  return r.data
}

//	///////////////////////
//	MARK: ANCHORS
export interface IAnchorPayload {
  headingId?: number | null
  subHeadingId?: number | null
  freeText?: string | null
  applicable?: boolean | null
  notApplicableReason?: string | null
}

export async function saveAnchor(
  documentId: number,
  itemId: number,
  payload: IAnchorPayload,
): Promise<IAnchorRow> {
  const r = await axios.put<IAnchorRow>(AUTHORITY_ENDPOINTS.checklistAnchor, {
    documentId,
    itemId,
    ...payload,
  })
  return r.data
}

export async function clearAnchor(documentId: number, itemId: number): Promise<boolean> {
  const r = await axios.delete<{ cleared: boolean }>(AUTHORITY_ENDPOINTS.checklistAnchor, {
    params: { documentId, itemId },
  })
  return r.data.cleared
}

//	///////////////////////
//	MARK: IMPORTS
export async function importChecklistTemplate(
  file: File,
  code: string,
  details: {
    name?: string
    reference?: string
    authorityId?: number | null
    publishedOn?: string | null
  },
): Promise<IChecklistImportResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('code', code)
  if (details.name) form.append('name', details.name)
  if (details.reference) form.append('reference', details.reference)
  if (details.authorityId) form.append('authorityId', String(details.authorityId))
  if (details.publishedOn) form.append('publishedOn', details.publishedOn)

  // 409 = checklist déjà ancrée : rapporté comme un résultat, pas comme une panne.
  const r = await axios.post<IChecklistImportResult>(AUTHORITY_ENDPOINTS.checklistImport, form, {
    validateStatus: (status) => status === 200 || status === 409,
  })
  return r.data
}

export async function importAnchors(
  file: File,
  documentId: number,
  code: string,
  apply: boolean,
  manualColumn?: string,
): Promise<IAnchorImportReport> {
  const form = new FormData()
  form.append('file', file)
  form.append('documentId', String(documentId))
  form.append('code', code)
  form.append('apply', String(apply))
  if (manualColumn) form.append('manualColumn', manualColumn)

  const r = await axios.post<IAnchorImportReport>(AUTHORITY_ENDPOINTS.checklistImportAnchors, form)
  return r.data
}

export async function importSubmissionHistory(
  file: File,
  authorityId: number,
  apply: boolean,
): Promise<IHistoryImportResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('authorityId', String(authorityId))
  form.append('apply', String(apply))

  const r = await axios.post<IHistoryImportResult>(
    AUTHORITY_ENDPOINTS.submissionHistoryImport,
    form,
  )
  return r.data
}

//	///////////////////////
//	MARK: DOWNLOADS
function saveBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(objectUrl)
}

function fileNameFromDisposition(headers: unknown, fallback: string): string {
  const disposition = String((headers as Record<string, unknown>)?.['content-disposition'] ?? '')
  return /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallback
}

export async function downloadTransmittalLetterPdf(submissionId: number): Promise<void> {
  const r = await axios.get<Blob>(`${AUTHORITY_ENDPOINTS.submission}/${submissionId}/letter/pdf`, {
    responseType: 'blob',
  })
  saveBlob(r.data, fileNameFromDisposition(r.headers, `transmittal-${submissionId}.pdf`))
}

export async function downloadFilledChecklist(
  revisionId: number,
  code?: string | null,
): Promise<void> {
  const r = await axios.get<Blob>(AUTHORITY_ENDPOINTS.checklistGenerate, {
    params: { revisionId, code: code || undefined },
    responseType: 'blob',
  })
  saveBlob(r.data, fileNameFromDisposition(r.headers, 'checklist'))
}

export interface IStoredFilePreviewSource {
  url: string
  mimeType: string | null
  // URL objet à révoquer à la fermeture de l'aperçu (blobs legacy) — les presigned S3 non.
  objectUrl: boolean
}

// La clé S3 vient de la vue : relire un fichier par son id n'est ouvert qu'à son uploader et à
// l'admin. Le repli par l'id ne sert plus qu'à un fichier sans clé (blob resté en base).
export interface IStoredFileRef {
  fileId: number
  fileKey?: string | null
  mimeType?: string | null
}

async function presignedUrlOf(fileKey: string): Promise<string | null> {
  const presigned = await axios.get<{ fileUrl?: string }>(
    `/api/digitalFile/presignedUrl?hash=${encodeURIComponent(fileKey)}`,
  )

  return presigned.data.fileUrl ?? null
}

// Même canal que openStoredFile, mais rend une URL affichable (img/iframe) au lieu
// d'ouvrir ou de télécharger : presigned S3 tel quel, blob legacy converti en URL objet.
export async function fetchStoredFilePreview(
  file: IStoredFileRef,
): Promise<IStoredFilePreviewSource | null> {
  if (file.fileKey) {
    const url = await presignedUrlOf(file.fileKey)

    return url ? { url, mimeType: file.mimeType ?? null, objectUrl: false } : null
  }

  const r = await axios.get<IDigitalFileWContent & { awsS3KeyString?: string }>(
    `/api/digitalFile/${file.fileId}`,
  )

  if (r.data.awsS3KeyString) {
    const presigned = await axios.get<{ fileUrl?: string }>(
      `/api/digitalFile/presignedUrl?hash=${encodeURIComponent(r.data.awsS3KeyString)}`,
    )

    return presigned.data.fileUrl
      ? { url: presigned.data.fileUrl, mimeType: r.data.mimeType ?? null, objectUrl: false }
      : null
  }

  const parsed = convertDigitalFileToBase64(r.data)

  if (!parsed.base64) return null

  const blob = await (await fetch(parsed.base64)).blob()

  return {
    url: URL.createObjectURL(blob),
    mimeType: parsed.mimeType ?? r.data.mimeType ?? null,
    objectUrl: true,
  }
}

// Les quatre pièces d'une soumission sont des DigitalFile : blob legacy (fileContent) ou S3.
export async function openStoredFile(file: IStoredFileRef): Promise<void> {
  const { fileId, fileKey } = file

  if (fileKey) {
    const url = await presignedUrlOf(fileKey)

    if (url) {
      window.open(url, '_blank', 'noopener')
    }

    return
  }

  const r = await axios.get<IDigitalFileWContent & { awsS3KeyString?: string }>(
    `/api/digitalFile/${fileId}`,
  )

  if (r.data.awsS3KeyString) {
    const presigned = await axios.get<{ fileUrl?: string }>(
      `/api/digitalFile/presignedUrl?hash=${encodeURIComponent(r.data.awsS3KeyString)}`,
    )

    if (presigned.data.fileUrl) {
      window.open(presigned.data.fileUrl, '_blank', 'noopener')
      return
    }
  }

  const parsed = convertDigitalFileToBase64(r.data)

  if (!parsed.base64) return

  const anchor = document.createElement('a')
  anchor.href = parsed.base64
  anchor.download = parsed.fileName ?? `file-${fileId}`
  anchor.click()
}
